import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { app } from '../firebase-config';
import QuotesCard from '../components/QuotesCard'; 
import { Capacitor } from '@capacitor/core';
import './SingleQuotePage.css'; 

const db = getFirestore(app);

const SingleQuotePage = () => {
    const { quoteId } = useParams();
    const navigate = useNavigate();
    const [quotesList, setQuotesList] = useState([]); // Stores the complete dynamic card set
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isApp, setIsApp] = useState(false);

    useEffect(() => {
        setIsApp(Capacitor.isNativePlatform());
    }, []);

    useEffect(() => {
        const fetchQuoteAndSet = async () => {
            if (!quoteId) {
                setError('No quote ID provided.');
                setLoading(false);
                return;
            }

            try {
                // 1. Fetch the single shared card document
                const docRef = doc(db, 'quotes', quoteId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const sharedQuote = { id: docSnap.id, ...docSnap.data() };
                    const targetSetId = sharedQuote.setId || 'set_1';

                    // 2. Query all other quotes sharing the same Set ID
                    const q = query(
                        collection(db, 'quotes'),
                        where('setId', '==', targetSetId)
                    );
                    
                    const setSnapshot = await getDocs(q);
                    const setList = [];
                    setSnapshot.forEach(doc => {
                        setList.push({ id: doc.id, ...doc.data() });
                    });

                    // Sort client-side by creation date to avoid requiring manual database index keys
                    // Locate step 2 sorting block inside SingleQuotePage.js and replace it with:
                    setList.sort((a, b) => {
                        const orderA = typeof a.orderIndex === 'number' ? a.orderIndex : 0;
                        const orderB = typeof b.orderIndex === 'number' ? b.orderIndex : 0;
                        return orderA - orderB;
                    });

                    // Ensure shared card is in the array list
                    if (!setList.some(item => item.id === sharedQuote.id)) {
                        setList.unshift(sharedQuote);
                    }

                    // 3. Rotate the array list so that our shared quote begins at index 0
                    const startIndex = setList.findIndex(item => item.id === sharedQuote.id);
                    const rotatedSet = [
                        ...setList.slice(startIndex),
                        ...setList.slice(0, startIndex)
                    ];

                    setQuotesList(rotatedSet);
                } else {
                    setError('This quote could not be found.');
                }
            } catch (err) {
                console.error("Error fetching quote set:", err);
                setError('Failed to load the quote set.');
            } finally {
                setLoading(false);
            }
        };

        fetchQuoteAndSet();
    }, [quoteId]);

    const handleOpenInApp = () => {
        if (/Android/i.test(navigator.userAgent)) {
            window.location.href = `intent://quote/${quoteId}#Intent;scheme=ainews;package=com.pragathipublications.ainews;S.browser_fallback_url=${encodeURIComponent(window.location.href)};end`;
        } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            window.location.href = `ainews://quote/${quoteId}`;
        }
    };

    const handleBack = () => {
        if (isApp) {
            navigate('/');
            return;
        }

        const hasPreviousPage = document.referrer && document.referrer.includes(window.location.host);

        if (window.history.length > 1 && hasPreviousPage) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const redirectToAppStore = () => {
        if (Capacitor.getPlatform() === 'android') {
            window.open('market://details?id=com.pragathipublications.ainews', '_system');
        } else if (Capacitor.getPlatform() === 'ios') {
            window.open('https://apps.apple.com/app/idYOUR_APP_ID', '_system'); 
        } else {
            window.open('https://ainewsapp.net', '_blank');
        }
    };

    return (
        <div className="single-quote-page-container">
            <button className="back-button" onClick={handleBack}>
                &larr; {isApp ? 'Home' : 'Back'}
            </button>

            <div className="quote-content">
                {loading && <div className="loader">Loading quote set...</div>}
                {error && <div className="error-message">{error}</div>}
                
                {quotesList.length > 0 && (
                    <>
                        {/* Swiper carousels are completely functional inside web & app */}
                        <QuotesCard quotes={quotesList} isActive={true} />
                        
                        {!isApp && (
                            <button onClick={handleOpenInApp} className="open-in-app-button">
                                ⚡ Open in Ai News App
                            </button>
                        )}

                        {!isApp && (
                            <div className="app-download-prompt">
                                <h3>🚀 Get the full experience!</h3>
                                <p>Download our app to browse quotes, get personalized feeds, and more.</p>
                                <button onClick={redirectToAppStore} className="download-app-button">
                                    Download App
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default SingleQuotePage;